import { useSurfaceProps } from '../surfaces/util/SurfaceProvider'
import React from 'react'
import {
  GeckoComponentCoords,
  GeckoManway,
  GeckoMeasurement,
  GeckoMeasurementUnit,
} from 'types'

import { FeatureBoltRing3D } from './procedural/FeatureBoltRing3D'
import { FeatureCylinder3D } from './procedural/FeatureCylinder3D'
import { FeatureLabel3D } from './procedural/FeatureLabel3D'
import { SurfaceFeature3D } from './SurfaceFeature3D'

/**
 * A component representing a manway on the surface of a component
 * @param model              The manway model (from the asset model)
 * @constructor
 */
export const ManwayFeature3D: React.FC<{
  model: GeckoManway
}> = ({ model }) => {
  //----------------------------------------------------------------------------

  const { bounds } = useSurfaceProps()

  const repadRadius = React.useMemo(
    () => model?.repadDiameter?.scale(0.5) ?? model.radius.scale(1.5),
    [model.radius, model.repadDiameter]
  )
  const flangeRadius = React.useMemo(
    () =>
      model.radius.plus(
        GeckoMeasurement.CreateFromValue(0.025, model.radius.unit)
      ),
    [model.radius]
  )
  const repadThickness = GeckoMeasurement.CreateFromValue(
    0.005,
    GeckoMeasurementUnit.Meters
  )
  const protrusion = React.useMemo(() => 0.1, [])

  //----------------------------------------------------------------------------

  const { labelCoords, positionLabelAbove } = React.useMemo(() => {
    const x = model.geometry.x
    let y = model.geometry.y
      .plus(repadRadius)
      .plus(GeckoMeasurement.CreateFromValue(1, GeckoMeasurementUnit.Inches))
    const positionAbove =
      bounds.y.end.minus(y).num > bounds.height.scale(1 / 10).num
    if (!positionAbove) {
      y = model.geometry.y.minus(repadRadius)
    }
    const labelCoords = GeckoComponentCoords.CreateFromMeasurements(
      bounds.componentID,
      x,
      y
    )
    return { labelCoords, positionLabelAbove: positionAbove }
  }, [model, repadRadius, bounds])

  //----------------------------------------------------------------------------

  return (
    <>
      <FeatureLabel3D
        feature={model}
        coords={labelCoords}
        anchor={positionLabelAbove ? 'bottom' : 'top'}
      />
      <SurfaceFeature3D model={model}>
        {/* Repad */}
        <FeatureCylinder3D
          feature={model}
          position={[0, 0, 0]}
          radius={repadRadius.num}
          height={repadThickness.num}
          debugColor={'blue'}
        />
        {/* Tube */}
        <FeatureCylinder3D
          feature={model}
          position={[0, repadThickness.num + 0.01, 0]}
          radius={model.radius.num}
          height={protrusion - 0.025 - repadThickness.num}
          debugColor={'purple'}
        />
        {/* Inner Flange */}
        <FeatureCylinder3D
          feature={model}
          position={[0, protrusion - 0.04, 0]}
          radius={flangeRadius.num}
          height={0.02}
          debugColor={'orange'}
        />
        {/* Outer Flange */}
        <FeatureCylinder3D
          feature={model}
          position={[0, protrusion - 0.02, 0]}
          radius={flangeRadius.num}
          height={0.02}
          debugColor={'red'}
        />
        {/* Flange bolts */}
        <FeatureBoltRing3D
          feature={model}
          position={[0, protrusion, 0]}
          radius={model.radius}
        />
      </SurfaceFeature3D>
    </>
  )
}
